import { GridSortModel, GridSortDirection } from '@mui/x-data-grid-pro';
import { DocumentationValueGetters } from 'components/common/dataGrid/columnsFactory/valueGetters';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { DirectoryModel } from 'shared/domain/directory/directoryModel';
import { DateTime } from 'luxon';
import { isNullish } from 'helpers/misc';
import { createLabelSortComparator } from '../columnsFactory/sortComparators';

type ComparableValue = string | number | Date | null | undefined;

enum SortDirection {
  ASC = 1,
  DESC = -1,
}

const toSortDirection = (direction: GridSortDirection): SortDirection =>
  direction === 'asc' ? SortDirection.ASC : SortDirection.DESC;

const naturalSortCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const normalizeNumericSeparators = (value: string): string =>
  value.replace(/(\d)[.,_-]+(?=\d)/g, '$1.');

const compareDates = (
  a: Date,
  b: Date,
  direction: GridSortDirection
): number => {
  const luxonA = DateTime.fromJSDate(a);
  const luxonB = DateTime.fromJSDate(b);
  const diff = luxonA.toMillis() - luxonB.toMillis();
  return diff * toSortDirection(direction);
};

const compareStrings = (
  a: string,
  b: string,
  direction: GridSortDirection
): number => {
  const normalizedA = normalizeNumericSeparators(a);
  const normalizedB = normalizeNumericSeparators(b);

  const result = naturalSortCollator.compare(normalizedA, normalizedB);

  if (result === 0) {
    const fallbackResult = a.localeCompare(b, undefined, {
      sensitivity: 'base',
    });
    return fallbackResult * toSortDirection(direction);
  }

  return result * toSortDirection(direction);
};

const compareNumbers = (
  a: number,
  b: number,
  direction: GridSortDirection
): number => {
  const diff = a - b;
  return diff * toSortDirection(direction);
};

function compareValues(
  a: ComparableValue,
  b: ComparableValue,
  direction: GridSortDirection
): number {
  if (isNullish(a) && isNullish(b)) return 0;
  if (isNullish(a)) return toSortDirection(direction);
  if (isNullish(b)) return -toSortDirection(direction);

  if (Array.isArray(a) && Array.isArray(b)) {
    const arrayComparator = createLabelSortComparator<{
      label: string;
    }>();
    const result = arrayComparator(a, b);
    return result * toSortDirection(direction);
  }

  if (a instanceof Date && b instanceof Date) {
    return compareDates(a, b, direction);
  }

  if (typeof a === 'string' && typeof b === 'string') {
    return compareStrings(a, b, direction);
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return compareNumbers(a, b, direction);
  }

  return 0;
}

export function sortByField<T extends DocumentationModel | DirectoryModel>(
  items: T[],
  field: string,
  direction: GridSortDirection,
  valueGetters: DocumentationValueGetters
): T[] {
  return [...items].sort((a, b) => {
    const valueA = valueGetters[field]
      ? valueGetters[field](field, a)
      : a[field];
    const valueB = valueGetters[field]
      ? valueGetters[field](field, b)
      : b[field];

    return compareValues(valueA, valueB, direction);
  });
}

/**
 * Get the main sort from the sort model
 * @param sortModel - The sort model
 * @returns The primary sort
 */
const getMainSort = (
  sortModel: GridSortModel
): { field: string; direction: GridSortDirection } | null => {
  const mainSort = sortModel[0];
  if (!mainSort) return null;

  return {
    field: mainSort.field,
    direction: mainSort.sort,
  };
};

export function sortItems<T extends DocumentationModel | DirectoryModel>(
  items: T[],
  sortModel: GridSortModel,
  valueGetters: DocumentationValueGetters
): T[] {
  if (!sortModel?.length) return items;

  const primarySort = getMainSort(sortModel);
  if (!primarySort?.direction) return items;

  return sortByField(
    items,
    primarySort.field,
    primarySort.direction,
    valueGetters
  );
}
